<template>
	<div class="project-details py-5 px-6 new-back full-height">
		<v-progress-linear v-if="loading" indeterminate color="primary" class="mb-0 rounded-0" />
		<v-row>
			<v-col cols="12">

				<v-card class="new-back elevation-0 bordered rounded-lg pa-4 mb-6">
					<div class="d-flex align-center justify-space-between flex-wrap mb-4">
						<h1 class="text-h5 font-weight-bold mb-0">{{ item.customerName }}</h1>
						<div v-if="!editing">
							<v-btn
								v-if="canEdit"
								color="primary"
								outlined
								@click="startEdit"
							>
								<v-icon left small>mdi-pencil</v-icon>
								Edit
							</v-btn>
						</div>
						<div v-else class="d-flex" style="gap: 8px">
							<v-btn text @click="cancelEdit">Cancel</v-btn>
							<v-btn
								color="primary"
								:disabled="!validForm || saving"
								:loading="saving"
								@click="saveProject"
							>
								Save
							</v-btn>
						</div>
					</div>

					<!-- Vista solo lectura: sin inputs -->
					<v-row v-if="!editing" class="project-details-fields-readonly">
						<v-col cols="12" md="6">
							<div class="detail-field-label">Name</div>
							<div class="detail-field-value">{{ item.name || '—' }}</div>
						</v-col>
						<v-col cols="12" md="6">
							<div class="detail-field-label">Type</div>
							<div v-if="projectTypeForView" class="mt-1">
								<div
									class="project-type-pill"
									:style="{ background: projectTypeForView.lightColor }"
								>
									<div class="letter" :style="{ background: projectTypeForView.color }">
										{{ projectTypeForView.small }}
									</div>	
									<div class="name">{{ projectTypeForView.text }}</div>
								</div>
							</div>
							<div v-else class="detail-field-value mt-1">—</div>
						</v-col>
						<v-col cols="12" md="6">
							<div class="detail-field-label">Delivery manager</div>
							<div v-if="item.deliveryManagerId" class="d-flex align-center mt-1">
								<user-avatar
									:user-id="item.deliveryManagerId"
									:size="36"
									:show-card="false"
									hide-loading
									class="mr-6 flex-shrink-0"
								/>
								<div class="ml-4">
									<div class="detail-field-value">{{ item.deliveryManagerBlueTag || '—' }}</div>
									<div v-if="item.deliveryManagerFullName" class="caption text--secondary">
										{{ item.deliveryManagerFullName }}
									</div>
								</div>
							</div>
							<div v-else class="detail-field-value mt-1">—</div>
						</v-col>
						<v-col cols="12" md="6">
							<div class="detail-field-label">Active</div>
							<div class="mt-1">
								<v-icon :color="item.active ? 'success' : 'error'">
									{{ item.active ? 'mdi-check-bold' : 'mdi-close-thick' }}
								</v-icon>
							</div>
						</v-col>
					</v-row>

					<v-form v-else v-model="validForm" ref="form">
						<v-row class="project-details-form-row">
							<v-col cols="12" md="6">
								<v-text-field
									v-model="edit.name"
									label="Name"
									outlined
									dense
									:rules="[v => !!(v && String(v).trim()) || 'Required']"
									hide-details="auto"
								/>
							</v-col>
							<v-col cols="12" md="6">
								<div class="detail-field-label">Type</div>
								<div v-if="projectTypeForView" class="mt-1">
									<div
										class="project-type-pill"
										:style="{ background: projectTypeForView.lightColor }"
									>
										<div class="letter" :style="{ background: projectTypeForView.color }">
											{{ projectTypeForView.small }}
										</div>
										<div class="name">{{ projectTypeForView.text }}</div>
									</div>
								</div>
								<div v-else class="detail-field-value mt-1">—</div>
							</v-col>
						<v-col cols="12" md="6">
							<q-user-autocomplete
								v-model="edit.deliveryManagerId"
								:confirmable="false"
								:items="users"
								:single-line="false"
								outlined
								dense
								label="Delivery manager"
								class="users"
								hide-append
							/>
						</v-col>
						<v-col cols="12" md="6">
							<div class="detail-field-label">Active</div>
							<check-switch v-model="edit.active"/>
						</v-col>
					</v-row>
					</v-form>
				</v-card>

				<v-card class="new-back elevation-0 bordered rounded-lg pa-4">
					<div class="d-flex align-center justify-space-between flex-wrap mb-3">
						<h2 class="text-h6 font-weight-bold mb-0">Assignments</h2>
						<v-btn
							small
							text
							color="primary"
							:to="assignmentsFilterLink"
						>
							<v-icon left small>mdi-open-in-new</v-icon>
							Open in Assignments
						</v-btn>
					</div>

					<v-progress-linear
						v-if="assignmentsLoading"
						indeterminate
						color="primary"
						class="mb-2 rounded-0"
					/>
					<assignments-table
						v-else
						v-model="selectedAssignments"
						class="project-assignments-table elevation-0 bordered rounded"
						:items="assignmentsList"
						:total-items="assignmentsTotal"
						:billable-lines="assignmentTableEmptyList"
						:billables="assignmentTableEmptyList"
						:users="assignmentTableEmptyList"
						:customer-projects="assignmentTableEmptyList"
						:infinite="false"
						readonly
						disable-sort
						disable-pagination
						hide-default-footer
						item-key="id"
						:hidden-headers="['customerProjectName']"
						@delete-assignment="onDeleteAssignmentReadonly"
					/>
				</v-card>
			</v-col>
		</v-row>
	</div>
</template>

<script>
import moment from 'moment'
import BillableJSON from '../../json/billable.json'
import AssignmentsTable from '../../components/admin-surf/assignments/AssignmentsTable.vue'

export default {
	title: 'Project',
	components: { AssignmentsTable },
	props: ['id'],
	data: () => ({
		loading: true,
		saving: false,
		editing: false,
		validForm: false,
		item: {},
		edit: {
			name: '',
			type: 0,
			deliveryManagerId: null,
			active: false,
		},
		backup: null,
		customerProjectTypes: [],
		users: [],
		canEdit: false,
		/** AdminSurfCustomers (27): detail → puede abrir `/admin/customer/:id` */
		canViewCustomerDetail: false,
		assignmentsLoading: false,
		assignmentsList: [],
		assignmentsTotal: 0,
		selectedAssignments: [],
		/**
		 * AssignmentsTable en readonly solo muestra datos de cada fila (API);
		 * no usa catálogos de billable / línea / usuario / proyecto (solo al editar).
		 */
		assignmentTableEmptyList: [],
		statuses: [
			{ text: 'Not started', value: 0, color: '#808080', lightColor: '#eee' },
			{ text: 'In Progress', value: 1, color: 'success', lightColor: '#e1f8dd' },
			{ text: 'Completed', value: 2, color: '#ab57ff', lightColor: '#dbb8ff' },
		],
	}),
	computed: {
		projectTypeForView() {
			const type = this.editing ? this.edit.type : this.item.type
			if (type === undefined || type === null) return null
			return this.customerProjectTypes.find(t => t.value === type) || null
		},
		assignmentsFilterLink() {
			return {
				path: '/admin/assignment',
				query: { customerProject: this.id },
			}
		},
	},
	created() {
		this.customerProjectTypes = BillableJSON.projectTypes
		this.$security.hasRequiredPermission('edit').then(res => {
			this.canEdit = res
		})
		// Misma regla que CustomerList (ver cliente)
		this.$security.hasRequiredPermission('detail', 27).then(res => {
			this.canViewCustomerDetail = res
		})
	},
	mounted() {
		document.title = 'Admin Project'
		this.$q.changeHeaderTitle('Admin Project')
		this.loadUsers()
		this.load()
		if (this.id && this.id !== 'new') {
			this.fetchAssignmentsForProject()
		}
	},
	methods: {
		fetchAssignmentsForProject() {
			if (!this.id || this.id === 'new') return Promise.resolve()
			this.assignmentsLoading = true
			return this.$http
				.post('/api/assignment/list', {
					search: '',
					users: [],
					billables: [],
					billableLines: [],
					customers: [],
					customerProjects: [this.id],
					projectTypes: [],
					quantity: null,
					isBillable: null,
					status: [],
					startDate: null,
					endDate: null,
					page: 1,
					pageSize: -1,
				})
				.then(res => {
					const raw = res.data.assignments || res.data.Assignments || []
					const list = raw.filter(a => !a.deleted)
					list.forEach(a => this.setStatus(a))
					this.assignmentsList = list
					this.assignmentsTotal =
						res.data.total !== undefined ? res.data.total : res.data.Total !== undefined ? res.data.Total : list.length
				})
				.catch(() => this.$root.error('Cannot load assignments'))
				.finally(() => {
					this.assignmentsLoading = false
				})
		},
		setStatus(assignment) {
			const today = moment().startOf('day')
			this.$set(assignment, 'timeStatus', 0)
			if (today.isBefore(assignment.startDate)) {
				assignment.timeStatus = 0
			} else if (today.isSameOrBefore(assignment.endDate)) {
				assignment.timeStatus = 1
			} else {
				assignment.timeStatus = 2
			}
			const status = this.statuses.find(x => x.value === assignment.timeStatus) || this.statuses[0]
			this.$set(assignment, 'timeStatusName', status.text)
			this.$set(assignment, 'timeStatusColor', status.color)
			this.$set(assignment, 'timeStatusLightColor', status.lightColor)
		},
		onDeleteAssignmentReadonly() {
			this.$root.success('Open Assignments to delete or edit assignments.')
		},
		loadUsers() {
			this.$http
				.get('api/user?active=true&onlyInterns=true')
				.then(res => {
					this.users = res.data
				})
				.catch(() => this.$root.error('Cannot load users'))
		},
		load() {
			if (!this.id || this.id === 'new') {
				this.loading = false
				return Promise.resolve()
			}
			this.loading = true
			return this.$http
				.get(`api/CustomerProject/${this.id}`)
				.then(res => {
					this.item = res.data
					this.syncEditFromItem()
				})
				.catch(() => this.$root.error('Cannot load project'))
				.finally(() => {
					this.loading = false
				})
		},
		syncEditFromItem() {
			this.edit = {
				name: this.item.name || '',
				type: this.item.type,
				deliveryManagerId: this.item.deliveryManagerId || null,
				active: this.item.active ?? false,
			}
		},
		startEdit() {
			this.backup = { ...this.edit }
			this.editing = true
		},
		cancelEdit() {
			if (this.backup) {
				this.edit = { ...this.backup }
				this.backup = null
			} else this.syncEditFromItem()
			this.editing = false
		},
		saveProject() {
			if (!this.$refs.form.validate()) return
			this.saving = true
			const body = {
				id: this.item.id,
				name: this.edit.name.trim(),
				type: this.edit.type,
				deliveryManagerId: this.edit.deliveryManagerId,
				active: this.edit.active,
			}
			this.$http
				.put(`api/CustomerProject/${this.id}`, body)
				.then(() => {
					this.$root.success('Project updated')
					this.editing = false
					this.backup = null
					return this.load().then(() => this.fetchAssignmentsForProject())
				})
				.catch(() => this.$root.error('Cannot save project'))
				.finally(() => {
					this.saving = false
				})
		},
	},
}
</script>

<style lang="scss" scoped>
.project-type-pill {
	display: inline-flex;
	align-items: center;
	border-radius: 15px;
	white-space: nowrap;
	min-height: 30px;
	.letter {
		width: 30px;
		height: 30px;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		color: white;
		border-radius: 15px;
		font-size: 12px;
	}
	.name {
		padding: 0 10px 0 6px;
		color: rgba(0, 0, 0, 0.87);
	}
}
.theme--dark .project-type-pill .name {
	color: rgba(255, 255, 255, 0.9);
}
.project-details-fields-readonly,
.project-details-form-row {
	.detail-field-label {
		font-size: 0.75rem;
		font-weight: 500;
		letter-spacing: 0.02em;
		color: rgba(0, 0, 0, 0.6);
		margin-bottom: 4px;
	}
	.detail-field-value {
		font-size: 1rem;
		font-weight: 500;
		line-height: 1.4;
		color: rgba(0, 0, 0, 0.87);
	}
}
.theme--dark .project-details-fields-readonly,
.theme--dark .project-details-form-row {
	.detail-field-label {
		color: rgba(255, 255, 255, 0.6);
	}
	.detail-field-value {
		color: rgba(255, 255, 255, 0.95);
	}
}
.project-assignments-table ::v-deep .v-data-table__wrapper {
	max-height: min(70vh, 900px);
}
.users ::v-deep .v-select__selections {
	flex-wrap: nowrap !important;
}
</style>
